/**
 * Dashboard UI tokens derived from shift status palettes where possible.
 */

import type { DashboardStatusPillStyle } from "@/domain/shifts";
import {
  SHIFT_STATUS_COLORS,
  dashboardPillStyleFromStatusColors
} from "@/domain/shifts";
import type {
  ProfessionalShiftStatus,
  ReferralStatus
} from "@/domain/professionals";
import {
  PROFESSIONAL_SHIFT_STATUS_COLORS,
  PROFESSIONAL_SHIFT_STATUS_LABELS
} from "@/domain/professionals";

export type DashboardRecentShiftStatusPill = DashboardStatusPillStyle & {
  label: string;
};

function withLabel(
  style: DashboardStatusPillStyle,
  label: string
): DashboardRecentShiftStatusPill {
  return { ...style, label };
}

/**
 * Practice dashboard “recent shift” rows: map copy to practice shift lifecycle colors.
 * `scheduled` → active (blue), `staff-needed` → upcoming (amber).
 */
export const PRACTICE_DASHBOARD_RECENT_SHIFT_STATUS_CONFIG = {
  completed: withLabel(
    dashboardPillStyleFromStatusColors(SHIFT_STATUS_COLORS.completed),
    "Completed"
  ),
  scheduled: withLabel(
    dashboardPillStyleFromStatusColors(SHIFT_STATUS_COLORS.active),
    "Scheduled"
  ),
  "staff-needed": withLabel(
    dashboardPillStyleFromStatusColors(SHIFT_STATUS_COLORS.upcoming),
    "Staff Needed"
  )
} as const;

export type PracticeDashboardRecentShiftStatus =
  keyof typeof PRACTICE_DASHBOARD_RECENT_SHIFT_STATUS_CONFIG;

const PROFESSIONAL_DASHBOARD_STATUS_KEYS = [
  "applied",
  "upcoming",
  "active",
  "completed",
  "withdrawn"
] as const satisfies readonly ProfessionalShiftStatus[];

export const PROFESSIONAL_DASHBOARD_RECENT_SHIFT_STATUS_CONFIG: Record<
  string,
  DashboardRecentShiftStatusPill
> = Object.fromEntries(
  PROFESSIONAL_DASHBOARD_STATUS_KEYS.map((key) => [
    key,
    withLabel(
      dashboardPillStyleFromStatusColors(
        PROFESSIONAL_SHIFT_STATUS_COLORS[key]
      ),
      PROFESSIONAL_SHIFT_STATUS_LABELS[key]
    )
  ])
) as Record<string, DashboardRecentShiftStatusPill>;

/** Hex values for inline-only metrics (fallback when classes are not used). */
export const DASHBOARD_ACCENT_HEX = {
  blue: SHIFT_STATUS_COLORS.active.textLight,
  emerald: SHIFT_STATUS_COLORS.completed.textLight,
  amber: SHIFT_STATUS_COLORS.upcoming.textLight,
  /** Revenue / earnings cards — outside shift lifecycle palette */
  violet: "#8B5CF6"
} as const;

/** Solid strip + icon well (includes dark variants). */
export const DASHBOARD_ACCENT_BG_CLASS = {
  blue: SHIFT_STATUS_COLORS.active.badgeClasses,
  emerald: SHIFT_STATUS_COLORS.completed.badgeClasses,
  amber: SHIFT_STATUS_COLORS.upcoming.badgeClasses,
  violet: "bg-[#8B5CF6] dark:bg-[#7C3AED]"
} as const;

export const DASHBOARD_TREND_BADGE_CLASS = {
  positive: SHIFT_STATUS_COLORS.completed.chipClasses,
  negative: SHIFT_STATUS_COLORS.cancelled.chipClasses
} as const;

/** Mini stat icon wells: theme-aware surfaces + icon colors. */
export const PRACTICE_SHIFT_SUMMARY_MINI_STAT_STYLES = {
  activeShifts: dashboardPillStyleFromStatusColors(SHIFT_STATUS_COLORS.active),
  scheduledToday: dashboardPillStyleFromStatusColors(
    SHIFT_STATUS_COLORS.completed
  ),
  staffNeeded: dashboardPillStyleFromStatusColors(SHIFT_STATUS_COLORS.upcoming)
} as const;

export const PROFESSIONAL_SHIFT_SUMMARY_MINI_STAT_STYLES = {
  completed: dashboardPillStyleFromStatusColors(
    PROFESSIONAL_SHIFT_STATUS_COLORS.completed
  ),
  upcoming: dashboardPillStyleFromStatusColors(
    PROFESSIONAL_SHIFT_STATUS_COLORS.upcoming
  ),
  pendingApplications: dashboardPillStyleFromStatusColors(
    PROFESSIONAL_SHIFT_STATUS_COLORS.applied
  )
} as const;

/** Teaser row count for dashboard cards that page a larger list elsewhere. */
export const DASHBOARD_CARD_PREVIEW_COUNT = 3;

/** Page size for the expanded professional referrals list. */
export const REFERRALS_PAGE_SIZE = 10;

/**
 * Practice dashboard “today” shift queries: large enough to cover a busy day
 * without pulling the full history.
 */
export const DASHBOARD_TODAY_SHIFT_PAGE_SIZE = 50;

/** Tailwind classes for referral status text on dashboard / tracker rows. */
export const REFERRAL_STATUS_STYLES: Record<ReferralStatus, string> = {
  active: "text-muted-foreground",
  completed: "text-success",
  cancelled: "text-muted-foreground line-through"
};

/** Compact hour row labels for the professional schedule overview weekly mini-grid. */
export const SCHEDULE_OVERVIEW_TIME_BLOCKS = [
  "8AM",
  "9AM",
  "10AM",
  "11AM",
  "12PM",
  "1PM",
  "2PM",
  "3PM",
  "4PM",
  "5PM"
] as const;
